import {
  ApplicantStatus,
  ExecutionModes,
  VerificationNames,
  VerificationStatus
} from "shared/types";
import { AlertCircle, Loading02, Minus } from "untitledui-js-base";
import { useQuery } from "@tanstack/react-query";
import { CreditReport, VerificationReport } from "types/landlord/reports";
import api from "api/landlord/applications";
import { errorHitCodes } from "views/applications-id-report/slices/CreditReportSlice/utils";
import { cx } from "utils/classnames";

const LoadingIcon = ({ isSelected }: { isSelected: boolean }) => (
  <Loading02
    className={cx(
      "stroke-content-100 w-4 h-4",
      isSelected ? "stroke-primary-500" : "stroke-content-100"
    )}
  />
);

const getStatusBasedIcon = (
  verificationStatus: VerificationStatus,
  isSelected: boolean
) => {
  switch (verificationStatus) {
    case VerificationStatus.Failed:
      return <AlertCircle className="stroke-danger-500 w-4 h-4" />;
    case VerificationStatus.FinishedWithWarnings:
      return <AlertCircle className="stroke-warning-600 w-4 h-4" />;
    case VerificationStatus.Processing:
    case VerificationStatus.Started:
    case VerificationStatus.Submitted:
      return <LoadingIcon isSelected={isSelected} />;
    case VerificationStatus.Pending:
    case VerificationStatus.Skipped:
      return <Minus className="stroke-content-100 w-4 h-4" />;
  }
};

const getIdentityVerificationIcon = (
  verificationStatus: VerificationStatus
) => {
  switch (verificationStatus) {
    case VerificationStatus.FinishedWithWarnings:
      return <AlertCircle className="stroke-warning-600" size="16px" />;
    case VerificationStatus.Failed:
      return <AlertCircle className="stroke-danger-500" size="16px" />;
  }
};

interface StatusIconProps {
  applicantStatus?: ApplicantStatus;
  verificationStatus: VerificationStatus;
  name: VerificationNames;
  executionMode?: ExecutionModes;
  isManuallyOverridden: boolean;
  isIdentityVerificationOverrideRequired: boolean;
  applicationId: number;
  applicantId: number;
  verificationId: number;
  isSelected: boolean;
  isRerun: boolean;
  isAwaitingForCounty?: boolean;
  isSsnMismatched?: boolean;
  hasWarnings?: boolean;
  hasActiveReverifyRequest?: boolean;
  hasProcessingIncome?: boolean;
}

export const StatusIcon = ({
  applicantStatus,
  verificationStatus,
  name,
  executionMode,
  isManuallyOverridden,
  isIdentityVerificationOverrideRequired,
  applicationId,
  applicantId,
  verificationId,
  isSelected,
  isRerun,
  isAwaitingForCounty,
  isSsnMismatched,
  hasWarnings = false,
  hasActiveReverifyRequest = false,
  hasProcessingIncome = false
}: StatusIconProps) => {
  const { data: creditReport } = useQuery<VerificationReport<CreditReport>>({
    queryKey: ["CREDIT_REPORT", applicationId, verificationId, applicantId],
    queryFn: () =>
      api
        .getApplicantVerification(applicationId, applicantId, verificationId)
        .then((res) => res.verification),
    enabled: name === VerificationNames.CreditReport
  });

  if (
    isAwaitingForCounty ||
    (name === VerificationNames.CombinedIncome &&
      (hasActiveReverifyRequest || hasProcessingIncome))
  )
    return <Loading02 className="w-4 h-4 stroke-warning-500" />;

  if (isManuallyOverridden) return;

  if (
    isIdentityVerificationOverrideRequired &&
    [
      VerificationNames.CreditReport,
      VerificationNames.CriminalReport,
      VerificationNames.EvictionReport
    ].includes(name)
  )
    return <Minus className="stroke-content-100 w-4 h-4" />;

  if (
    (isRerun || applicantStatus === ApplicantStatus.Submitted) &&
    verificationStatus === VerificationStatus.Pending
  )
    return <LoadingIcon isSelected={isSelected} />;

  if (
    (verificationStatus === VerificationStatus.Pending &&
      executionMode === ExecutionModes.Manual &&
      applicantStatus === ApplicantStatus.Submitted) ||
    errorHitCodes.includes(creditReport?.report.hit_code?.code || "")
  )
    return <AlertCircle className="stroke-danger-500" size="16px" />;

  if (name === VerificationNames.IdentityVerification) {
    const idvIcon = getIdentityVerificationIcon(verificationStatus);
    if (idvIcon) return idvIcon;
  }

  if (hasWarnings || isSsnMismatched)
    return <AlertCircle className="stroke-warning-600 w-4 h-4" />;

  return getStatusBasedIcon(verificationStatus, isSelected);
};
